import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { listingApiServerFetchEvent } from '#/modules/listing/events/api/listingApiFetchEvent';
import { isListingExpired } from '#/modules/listing/helpers/listingTiming';
import type { viewEventReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { DynamicSearchEventsRefinements } from '#/modules/search/events/components/Refinements/DynamicSearchEventsRefinements';
import { userDashboardApiServerFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { DynamicNotFoundPage } from '#/pages/NotFound/DynamicNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { dynamic } from '#/utils/dynamic';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewEventPage } from './ViewEventPage';

const DynamicExpiredEventPage = dynamic(
  () =>
    import('#/modules/listing/events/pages/expired/ExpiredEventPage').then(
      (m) => ({ default: m.ExpiredEventPage }),
    ),
  { strategy: 'always' },
);

const DynamicUnpublishedEventPage = dynamic(
  () =>
    import('#/modules/listing/events/pages/unpublished/UnpublishedEventPage').then(
      (m) => ({ default: m.UnpublishedEventPage }),
    ),
  { strategy: 'always' },
);

export const handle = {
  searchRefinements: <DynamicSearchEventsRefinements />,
} satisfies LayoutHandleElements;

export async function loader({
  request,
  context,
  params: { eventIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewEventReferenceRoute>) {
  const { serverContext } = getServerRouterContext(context);

  const sharedHandle = {
    defaultSearchType: 'EVENT',
    showSearchRefinements: true,
    showDesktopSearch: true,
  } satisfies LayoutHandleProps;

  const [eventId] = eventIdDashSlug.split('-');

  try {
    const [event, listingExtras] = await Promise.all([
      listingApiServerFetchEvent({ request, context })(eventId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiServerFetchListingExtras({
            request,
            context,
          })([eventId])
        : null,
    ]);

    switch (event.type) {
      case 'EVENT': {
        return {
          type: isListingExpired({
            expires: event.expires,
            now: serverContext.now,
          })
            ? 'EXPIRED_EVENT'
            : event.type,
          event,
          locale: serverContext.locale,
          handle: {
            ...sharedHandle,
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: event.url,
          } satisfies LayoutHandleProps,
          saved: Boolean(listingExtras?.[eventId]?.saved),
          now: serverContext.now,
        } as const;
      }
      case 'UNPUBLISHED_EVENT':
        return idealistNotFound({
          type: event.type,
          unpublishedEvent: event,
          handle: sharedHandle,
        } as const);
    }
  } catch {
    return idealistNotFound({
      type: 'NOT_FOUND',
      handle: sharedHandle,
    } as const);
  }
}

export default function ViewEventHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  switch (loaderData.type) {
    case 'NOT_FOUND':
      return <DynamicNotFoundPage />;
    case 'EVENT':
      return (
        <RouteModule
          title={`${loaderData.event.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['EVENT']}`}
          ogImage={imageUrlForOgMeta({
            data: loaderData.event.org?.logo,
          })}
          description={loaderData.event.descriptionTruncated.slice(0, 200)}
          schemaMarkups={[
            loaderData.event.schemaEventMarkup?.[loaderData.locale],
            loaderData.event.schemaBreadcrumbListMarkup?.[loaderData.locale],
          ]}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.event.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.event.url[loaderData.event.locale],
          }}
        >
          <ViewEventPage
            event={loaderData.event}
            initiallySaved={loaderData.saved}
            now={loaderData.now}
          />
        </RouteModule>
      );

    case 'EXPIRED_EVENT':
      return (
        <RouteModule title={loaderData.event.name}>
          <DynamicExpiredEventPage
            event={loaderData.event}
            now={loaderData.now}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_EVENT':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <DynamicUnpublishedEventPage
            unpublishedEvent={loaderData.unpublishedEvent}
          />
        </RouteModule>
      );
  }
}
